import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { KeyRound } from 'lucide-react';
import { Button, EmptyState } from '../../../components';
import { cx } from '../../../lib/cx';
import { formatDate, formatDateTime } from '../../../lib/format';
import { GoogleIcon } from '../../auth/GoogleIcon';
import { SectionCard } from '../SectionCard';
import { LINK_INTENT_KEY, startGoogleLink, type LinkedAccount } from '../api';
import { useCanSetPassword, useLinkedAccounts, useUnlinkSocialAccount } from '../useAccount';

/**
 * Account → Sign-in methods: the ways this person can get into Rundown, and
 * the controls to add or remove one.
 *
 * Google is linked by going through Google, not by a button that calls a link
 * endpoint — see the comment above `startGoogleLink` in ../api.ts for why the
 * redirect is the only honest route in a browser. The flow leaves the app
 * entirely and comes back through /auth/callback, which is why this section
 * reads `?linked=google` to say how it went.
 *
 * The rule the screen exists to protect: an account must keep at least one way
 * in. nauth's unlink has no such guard of its own (it will happily remove the
 * only credential on the account), so Unlink is offered only once a password
 * exists, and Password offers to set a first one when it does not.
 */

function MethodRow({
  icon,
  name,
  detail,
  status,
  action,
}: {
  icon: ReactNode;
  name: string;
  detail: string;
  /** `unknown` while the answer is still loading: no chip beats a wrong chip. */
  status: 'on' | 'off' | 'unknown';
  action?: ReactNode;
}) {
  return (
    <li className="flex items-center justify-between gap-3 border-b border-line px-4 py-3 last:border-b-0">
      <div className="flex min-w-0 items-center gap-3">
        <span className="grid size-8 flex-none place-items-center rounded-lg border border-line bg-surface-2">
          {icon}
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2 fs-13 text-ink">
            <span className="truncate font-medium">{name}</span>
            {status !== 'unknown' && (
              <span
                className={cx(
                  'flex-none rounded-full px-2 py-0.5 fs-10 font-bold',
                  status === 'on' ? 'bg-success-tint text-success' : 'bg-surface-3 text-muted',
                )}
              >
                {status === 'on' ? 'In use' : 'Not set up'}
              </span>
            )}
          </div>
          <div className="mt-0.5 fs-11 text-muted">{detail}</div>
        </div>
      </div>
      {action !== undefined && <div className="flex-none">{action}</div>}
    </li>
  );
}

function googleDetail(account: LinkedAccount): string {
  const parts = [account.providerEmail ?? 'Google account', `linked ${formatDate(account.linkedAt)}`];
  if (account.lastUsedAt !== null) parts.push(`last used ${formatDateTime(account.lastUsedAt)}`);
  return parts.join(' · ');
}

/**
 * What the round trip through Google actually achieved, said in terms of what
 * is now true rather than what was attempted.
 *
 * The case worth the code: Google's chooser will happily hand back a different
 * address, and because linking is by verified email that does not link — it
 * signs you in as whoever owns it. Someone who does that is now looking at
 * another account, and the only useful thing to tell them is exactly that.
 */
type LinkOutcome = 'linked' | 'not-linked' | { readonly signedInAs: string; readonly wanted: string };

/**
 * Read the outcome once, then take `?linked=google` out of the URL.
 *
 * It has to be a snapshot rather than a live reading of the linked list: unlink
 * the account a moment later and a live one would start claiming Google was
 * never linked, which is both wrong about what happened and contradicted by the
 * row directly beneath it. Dropping the parameter also stops a refresh hours
 * later reporting a redirect that is long over.
 */
function useLinkOutcome(email: string, linked: boolean, ready: boolean): LinkOutcome | null {
  const [params, setParams] = useSearchParams();
  const [outcome, setOutcome] = useState<LinkOutcome | null>(null);
  const asked = params.get('linked') === 'google';

  useEffect(() => {
    if (!asked || !ready) return;

    let intent: string | null = null;
    try {
      intent = sessionStorage.getItem(LINK_INTENT_KEY);
      sessionStorage.removeItem(LINK_INTENT_KEY);
    } catch {
      // Private browsing, or storage disabled — the check below is simply skipped.
    }

    setOutcome(
      intent !== null && intent !== email
        ? { signedInAs: email, wanted: intent }
        : linked
          ? 'linked'
          : 'not-linked',
    );

    const next = new URLSearchParams(params);
    next.delete('linked');
    setParams(next, { replace: true });
  }, [asked, ready, email, linked, params, setParams]);

  return outcome;
}

function LinkOutcomeNotice({ outcome, email }: { outcome: LinkOutcome; email: string }) {
  if (typeof outcome === 'object') {
    return (
      <p role="status" className="mb-3 rounded-lg bg-warn-tint px-3 py-2 fs-12.5 text-warn">
        That Google address belongs to a different Rundown account, so you are now signed in as{' '}
        {outcome.signedInAs}. Nothing was linked to {outcome.wanted} — sign in as {outcome.wanted} and try
        again, choosing the Google account with that address.
      </p>
    );
  }

  if (outcome === 'linked') {
    return (
      <p role="status" className="mb-3 rounded-lg bg-success-tint px-3 py-2 fs-12.5 text-success">
        Google is linked. You can sign in with it from now on.
      </p>
    );
  }

  return (
    <p role="status" className="mb-3 rounded-lg bg-warn-tint px-3 py-2 fs-12.5 text-warn">
      Google was not linked. Rundown links the Google account with the same address as your Rundown account
      &mdash; {email} &mdash; so choose that one at Google&rsquo;s prompt.
    </p>
  );
}

export function SignInMethodsSection({ email }: { email: string }) {
  const linkedQuery = useLinkedAccounts();
  const canSetPasswordQuery = useCanSetPassword();
  const unlink = useUnlinkSocialAccount();

  const google = linkedQuery.data?.find((account) => account.provider === 'google') ?? null;
  const outcome = useLinkOutcome(email, google !== null, linkedQuery.data !== undefined);
  // Undefined while it loads: the unlink control stays out until the answer is
  // known rather than flashing an option that may be about to be withdrawn.
  const hasPassword = canSetPasswordQuery.data === undefined ? undefined : !canSetPasswordQuery.data;

  return (
    <SectionCard title="Sign-in methods" description="How you get into Rundown.">
      {linkedQuery.isPending ? (
        <p className="fs-12 text-muted">Loading…</p>
      ) : linkedQuery.error !== null ? (
        <EmptyState title="Could not load sign-in methods" description={linkedQuery.error.message} />
      ) : (
        <>
          {/* Unlinking is the one thing that can make the notice stale while
              the screen is still open — it is then describing a link the
              person has just undone. */}
          {outcome !== null && !unlink.isSuccess && <LinkOutcomeNotice outcome={outcome} email={email} />}

          {unlink.isError && (
            <p role="alert" className="mb-3 rounded-lg bg-danger-tint px-3 py-2 fs-12.5 text-danger">
              {unlink.error.message}
            </p>
          )}

          <ul className="-m-4">
            <MethodRow
              icon={<GoogleIcon className="h-4 w-4" />}
              name="Google"
              status={google === null ? 'off' : 'on'}
              detail={
                google === null ? `Sign in with the Google account for ${email}.` : googleDetail(google)
              }
              action={
                google === null ? (
                  <Button size="sm" onClick={() => void startGoogleLink(email)}>
                    Link Google
                  </Button>
                ) : hasPassword === true ? (
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={unlink.isPending}
                    onClick={() => unlink.mutate('google')}
                  >
                    {unlink.isPending ? 'Unlinking…' : 'Unlink'}
                  </Button>
                ) : hasPassword === false ? (
                  <span className="fs-11 text-muted">Set a password below to unlink</span>
                ) : undefined
              }
            />
            <MethodRow
              icon={<KeyRound size={15} strokeWidth={1.75} className="text-muted" aria-hidden="true" />}
              name="Password"
              status={hasPassword === undefined ? 'unknown' : hasPassword ? 'on' : 'off'}
              detail={
                hasPassword === false
                  ? 'No password on this account — Google is the only way in.'
                  : `Used with ${email}.`
              }
            />
          </ul>
        </>
      )}
    </SectionCard>
  );
}
